import { Box, TextField } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import andIcon from "../../assets/icons/and-icon.svg";
import andOricon from "../../assets/icons/and-or-icon.svg";
import errorIcon from "../../assets/icons/error-icon.svg";
import BackArrow from "../../assets/icons/left-pointing-arrow-pink.svg";
import plusRedIcon from "../../assets/icons/plus-red.svg";

import { useBlocker } from "react-router-dom";
import { OutletContext } from "../../App";
import { useAppMe, useCountryList } from "../../services/okta";
import {
  useCreateSegmentList,
  useDropDownList,
  useGetSegmentDetail,
} from "../../services/segment/index";
import DatasetDropdown from "../../shared/components/dataset-dropdown/DatasetDropdown";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import formatCreateSegmentPayload from "../../utils/formatCreateSegmentPayload";
import formatValuesSegment from "../../utils/formatValuesSegment";
import { BorderSecondary, CircularProgressCustom, PrimaryButton } from "../../utils/muiCustomTheme";
import "./CreateSegment.scss";
import DialogComponent from "./DialogComponent";
import DynamicNestedDropdownSegmentSection from "./DynamicNestedDropdownSegmentSection";
import InsightsWrap from "./InsightsWrap";
import { CreateSegmentsProps, FormDataSegment } from "./segment-interface";
import SegmentDialog from "./SegmentDialog";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

const STIRISTA_PREFIX = "Stirista-";

const CreateSegment: React.FC<CreateSegmentsProps> = () => {
  const { setAlert } = useOutletContext<OutletContext>();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();
  const { createLink } = useSimulationParams();
  const [stateRemoveIncludeSection, setStateRemoveIncludeSection] = useState();
  const [stateRemoveExcludeSection, setStateRemoveExcludeSection] = useState();
  const [isDiscardByClick, setIsDiscardByClick] = useState(false);

  const [showUnsavedWorkDialog, setShowUnsavedWorkDialog] = useState<any>({
    show: false,
    type: "discard",
  });
  const [isSegmentDialogOpen, setIsSegmentDialogOpen] = useState(false);

  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({});
  const sub = dataAppMe?.data?.user_id;

  // Fetch the list of countries available to the user.
  // This hook returns an array of country objects, each containing details like name, code, admin status, and tags.
  const { data: countries = [] } = useCountryList();

  // Extract all unique tags from the fetched countries.
  // We flatten the tags arrays from all countries into a single array and use a Set to remove duplicates.
  const countryTags = Array.from(new Set(countries?.flatMap((c: any) => c.tags) || []));

  // Check if the user has the "EH" (Air Care) tag in any of their assigned countries.
  // This boolean determines whether Air Care related items should be shown in the dropdown.
  const hasEHTag = countryTags.includes("EH");

  // Fetch the dropdown data (taxonomy/categories) for the segment builder.
  // This data contains the available attributes and filters for creating segments.
  const { data: dataDropdown, isPending: isPendingDataDropdown } = useDropDownList({});

  const filteredDataDropdown = React.useMemo(() => {
    if (!dataDropdown?.data) return dataDropdown;

    // Define labels that should ALWAYS be hidden regardless of user permissions
    const alwaysHideLabels = [
      "Frequency of purchase",
      "Purchase Value"
    ];

    const filterItems = (items: any[]): any[] => {
      return items
        .filter((item: any) => {
          const label = item.label || "";
          const value = item.value || "";

          // HIDE items containing always-hidden labels
          for (const hideLabel of alwaysHideLabels) {
            if (label.includes(hideLabel) || value.includes(hideLabel)) {
              //console.log(`Hiding item due to always-hidden label: ${hideLabel}`);
              return false; // CRITICAL: return false to HIDE the item
            }
          }

          // HIDE "Air Care" items if user doesn't have EH tag permission
          if (label.includes("Air Care") || value.includes("Air Care")) {
            //console.log(`Air Care item - showing: ${hasEHTag}`);
            return hasEHTag; // Show only if user has EH permission
          }

          // Show all other items by default
          return true;
        })
        .map((item: any) => {
          // Recursively filter children if they exist
          if (item.children && item.children.length > 0) {
            const filteredChildren = filterItems(item.children);
            // Only keep item if it has filtered children or is a leaf node
            return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
          }
          return item;
        })
        //.filter(Boolean); // Remove null items (parents with no children after filtering)
    };

    return {
      ...dataDropdown,
      data: filterItems(dataDropdown.data)
    };
  }, [dataDropdown, hasEHTag]);

  const {
    data: dataSegmentDetail,
    isPending: isPendingDataSegmentDetail,
    isFetching: isFetchingDataSegmentDetail,
  } = useGetSegmentDetail({
    query: {
      segmentId: id,
      tags:countryTags,
    },
    options: {
      enabled: !!id && countryTags.length > 0,
    },
  });

  const parseDataSegmentDetail: any = dataSegmentDetail?.data ? JSON.parse(dataSegmentDetail.data) : [];

  const {
    segmentName = "",
    build,
    country,
    count = 0,
    engaged = 0,
    reachable = 0,
    uniqueEmails = 0,
    brand,
    folder,
  } = parseDataSegmentDetail?.[0] || {};
  const parseBuild = JSON.parse(build || null);

  const { exclude = {}, include = {} } = parseBuild || {};
  const tempVal = useMemo(() => {
    return formatValuesSegment({
      country: country ?? "",
      brand: brand,
      folder: folder,
      user: sub,
      segmentName,
      exclude,
      include,
      type: id ? "edit" : "create",
      segmentId: id ? id : undefined,
      dataDropdown: filteredDataDropdown?.data || [],
    });
  }, [brand, country, exclude, filteredDataDropdown?.data, folder, id, include, segmentName, sub]);

  const methods = useForm<FormDataSegment>({
    defaultValues: tempVal,
  });

  const {
    reset,
    getValues,
    control,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    formState: { isDirty },
    watch,
  } = methods;

  const resetKey = useMemo(() => {
    const dropdownCount = Array.isArray(filteredDataDropdown?.data)
      ? filteredDataDropdown.data.length
      : 0;
    const segmentDataStr = typeof dataSegmentDetail?.data === "string" ? dataSegmentDetail.data : "";
    return `${id || "create"}|${sub || ""}|${dropdownCount}|${segmentDataStr.length}`;
  }, [dataSegmentDetail?.data, filteredDataDropdown?.data, id, sub]);
  const lastResetKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;
    reset(tempVal);
  }, [reset, resetKey, tempVal]);

  const blocker = useBlocker(isDiscardByClick ? false : isDirty);

  const {
    fields,
    append,
    replace,
    remove,
    insert
  } = useFieldArray({
    control,
    name: "sections",
  });

  const navigateOnMySegments = () => {
    navigate(createLink("/segments"));
  };

  const {
    fields: fieldsExclusions,
    append: appendExclusions,
    replace: replaceExclusions,
    remove: removeExclusions,
    insert: insertExclusions,
  } = useFieldArray({
    control,
    name: "exclusions",
  });

  // Initialize with empty string
  const [selectedTag, setSelectedTag] = useState<string>("");

  // Update selectedTag once data is available
  useEffect(() => {
    if (id) {
      const initialTag =
        parseBuild?.tag ||
        parseDataSegmentDetail?.[0]?.tag ||
        parseDataSegmentDetail?.[0]?.Tag ||
        "";
      setSelectedTag(initialTag);
    }
  }, [id, parseBuild, parseDataSegmentDetail]);

  const { mutate: createSegment, isPending: isPendingCreateSegment } = useCreateSegmentList({
    options: {
      onSuccess: (res: any) => {
        let isSuccess = res?.data?.["Audience Status"] === "Success";
        let message = res?.data?.["Audience message"];

        if (isSuccess) {
          navigateOnMySegments();
        } else {
          setAlert({
            alertType: "warning",
            alertMsg: message || res?.message || "Warning",
          });
        }
      },
      onError: (err: any) => {
        setIsDiscardByClick(false);
        console.error("@err", err);
      },
    },
  });

  const openSegmentDialog = () => {
    if (getValues(`sections.0.conditions`)?.length > 0) {
      setIsSegmentDialogOpen(true);
    } else {
      setShowUnsavedWorkDialog({
        type: "required-condition",
        show: true,
      });
    }
  };

  const closeSegmentDialog = () => {
    setIsSegmentDialogOpen(false);
  };

  const handleSubmitPrimary = () => {
    if (showUnsavedWorkDialog.type === "navigate") {
      blocker.reset?.();
    }
    setShowUnsavedWorkDialog({ show: false, type: "" });
    setIsDiscardByClick(false);
  };

  const parseSelectedPath = (path: string) => {
    const [country, brand, folder] = (path || "").split("/");
    return { country, brand, folder };
  };

  const handleSaveSegment = (data: any) => {
    setIsDiscardByClick(true);
    const { segmentName, sections, exclusions, user, type, segmentId, path } = data;
    const parsed = parseSelectedPath(path);

    createSegment(
      formatCreateSegmentPayload({
        country: parsed?.country,
        segmentName,
        sections,
        exclusions,
        user,
        brand: parsed?.brand,
        folder: parsed?.folder,
        type,
        segmentId,
        pSegmentName:tempVal.segmentName||segmentName,
        ...(type !== "check" && { tag: selectedTag }), // only include tag if type is NOT "check"
      })
    );
  };

  const handleCloneSection = (index: number, type: "sections" | "exclusions") => {
    const sectionData = getValues(`${type}.${index}`);
    if (type === "sections") {
      insert(index + 1, sectionData);
    } else {
      insertExclusions(index + 1, sectionData);
    }
  };


  // const handleMoveSection = (index: number, type: "sections" | "exclusions") => {
  //   const sectionData = getValues(`${type}.${index}`);
  //   if (type === "sections") {
  //     // add move option
  //     if (index === 0)  return;

  //     remove(index);
  //     insert(index - 1, sectionData);

  //   } else {
  //     // add move option
  //     if (index === 0) return;

  //     removeExclusions(index);
  //     insertExclusions(index - 1, sectionData);
  //   }
  // };

  const handleAddSection = () => {
    const newSection = {
      conditions: [],
      operator: (fields?.[1] as any)?.operator
        ? (fields?.[1] as any)?.operator
        : fields?.length
        ? "AND"
        : "",
    };

    append(newSection);
  };

  const handleAddExclusions = () => {
    const newSection = {
      conditions: [],
      operator: (fieldsExclusions?.[1] as any)?.operator
        ? (fieldsExclusions?.[1] as any)?.operator
        : fieldsExclusions?.length
        ? "AND"
        : "",
    };
    appendExclusions(newSection);
  };

  const updateLogic = (index: number, operator: "AND" | "OR") => {
    const updatedFields = getValues("sections").map((field) => {
      return { ...field, operator };
    });
    replace(updatedFields);
  };

  const updateLogicExclusion = (index: number, operator: "AND" | "OR") => {
    const updatedFields = getValues("exclusions").map((field) => {
      return { ...field, operator };
    });

    replaceExclusions(updatedFields);
  };

  // Handle confirm in modal
  const handleDiscardClick = () => {
    if (showUnsavedWorkDialog.type === "navigate") {
      blocker.proceed?.();
    }
    if (showUnsavedWorkDialog.type === "discard") {
      navigateOnMySegments(); // your existing discard flow
    }
    setShowUnsavedWorkDialog({ show: false, type: "" });
  };

  const handleDiscard = () => {
    if (isDirty) {
      setIsDiscardByClick(true);
      setShowUnsavedWorkDialog({
        type: "discard",
        show: true,
      });
    } else {
      navigateOnMySegments();
    }
  };

  // block navigation for un-saved work
  useEffect(() => {
    if (blocker.state === "blocked" && isDirty) {
      setShowUnsavedWorkDialog({
        show: true,
        type: "navigate", // distinguish from discard button
      });
    }
  }, [blocker.state, isDirty]);

  useEffect(() => {
    if (stateRemoveIncludeSection !== null && stateRemoveIncludeSection !== undefined) {
      remove(stateRemoveIncludeSection);
      setStateRemoveIncludeSection(null as any);
    }

    if (stateRemoveExcludeSection !== null && stateRemoveExcludeSection !== undefined) {
      removeExclusions(stateRemoveExcludeSection);
      setStateRemoveExcludeSection(null as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateRemoveIncludeSection, stateRemoveExcludeSection]);

  const isAndOrInclude = watch("sections")?.[1]?.operator;
  const isAndOrExclude = watch("exclusions")?.[1]?.operator;
  const watchedSegmentName = useWatch({ control, name: "segmentName" });
  const watchedSections = useWatch({ control, name: "sections" });
  const watchedExclusions = useWatch({ control, name: "exclusions" });
  const segmentNameInputRef = useRef<HTMLInputElement | null>(null);
  const stiristaSegmentPrefixAutoInserted = useRef(false);
  const prevShouldForceStiristaPrefix = useRef(false);

  const hasStiristaCondition = useMemo(() => {
    // Use reactive values when present; fallback to RHF store snapshot for consistency.
    const sectionsSnapshot = Array.isArray(watchedSections) ? watchedSections : getValues("sections");
    const exclusionsSnapshot = Array.isArray(watchedExclusions) ? watchedExclusions : getValues("exclusions");

    const conditionsFromSections =
      sectionsSnapshot?.flatMap((s: any) => s?.conditions || []) || [];
    const conditionsFromExclusions =
      exclusionsSnapshot?.flatMap((s: any) => s?.conditions || []) || [];
    // Exclusions are treated as active only when exclusions block is visible in UI.
    const includeHasAnyConditions = (getValues("sections.0.conditions")?.length ?? 0) > 0;
    const exclusionsVisible = includeHasAnyConditions && (fieldsExclusions?.length ?? 0) > 0;

    const matchesStirista = (c: any) => {
      // Primary signal from payload flag + defensive fallbacks for older payload shapes.
      const isStiristaFlag = c?.isStirista === true;
      const pathStr = Array.isArray(c?.path) ? c.path.map((p: any) => typeof p === "string" ? p : p.label).join("/") : "";
      const selectedValue = typeof c?.selectedValue === "string" ? c.selectedValue : "";
      const parentPathStr = Array.isArray(c?.parentPath) ? c.parentPath.map((p: any) => typeof p === "string" ? p : p.label).join("/") : "";
      const tableStr = typeof c?.table === "string" ? c.table : "";
      const fieldStr = typeof c?.field === "string" ? c.field : "";

      const hasMatch =
        isStiristaFlag ||
        /stirista/i.test(pathStr) ||
        /stirista/i.test(selectedValue) ||
        /stirista/i.test(parentPathStr) ||
        /stirista/i.test(tableStr) ||
        /stirista/i.test(fieldStr);
      return hasMatch;
    };

    const includeHasStirista = conditionsFromSections.some(matchesStirista);
    const excludeHasStirista = exclusionsVisible ? conditionsFromExclusions.some(matchesStirista) : false;

    return includeHasStirista || excludeHasStirista;
  }, [fieldsExclusions?.length, getValues, watchedExclusions, watchedSections]);

  const shouldForceStiristaPrefix = hasStiristaCondition;

  useEffect(() => {
    const wasForced = prevShouldForceStiristaPrefix.current;

    // Turn forcing ON: ensure prefix exists and place caret after prefix.
    if (!wasForced && shouldForceStiristaPrefix) {
      const current = typeof watchedSegmentName === "string" ? watchedSegmentName : "";
      if (!current.startsWith(STIRISTA_PREFIX)) {
        const next = `${STIRISTA_PREFIX}${current}`.slice(0, 100);
        setValue("segmentName", next, { shouldDirty: true });
        stiristaSegmentPrefixAutoInserted.current = true;
        requestAnimationFrame(() => {
          const el = segmentNameInputRef.current;
          if (!el) return;
          const pos = Math.min(STIRISTA_PREFIX.length, el.value.length);
          el.setSelectionRange(pos, pos);
        });
      }
    }

    if (wasForced && !shouldForceStiristaPrefix) {
      // Turn forcing OFF: remove auto-prefix when Stirista context no longer applies.
      const current = typeof watchedSegmentName === "string" ? watchedSegmentName : "";
      if (current === STIRISTA_PREFIX) {
        setValue("segmentName", "", { shouldDirty: true });
      } else if (current.startsWith(STIRISTA_PREFIX)) {
        const next = current.slice(STIRISTA_PREFIX.length);
        setValue("segmentName", next, { shouldDirty: true });
      }
      stiristaSegmentPrefixAutoInserted.current = false;
    }

    prevShouldForceStiristaPrefix.current = shouldForceStiristaPrefix;
  }, [setValue, shouldForceStiristaPrefix, watchedSegmentName]);

  if (
    isPendingDataDropdown ||
    isPendingAppMe ||
    (isFetchingDataSegmentDetail && isPendingDataSegmentDetail)
  ) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="create-segment">
        <div ref={sentinelRef}></div>
        <div ref={headerRef} className={`create-segment__wrap ${isSticky ? "is-sticky" : ""}`}>
          <div className="create-segment__wrap__heading-wrap" onClick={handleDiscard}>
            <div className="create-segment__wrap__heading-wrap__icon">
              <img src={BackArrow} alt="Reckitt" />
            </div>
            <div className="create-segment__wrap__heading-wrap__heading">Back to Segments</div>
          </div>
          <div className="create-segment__wrap__btn-wrap">
            <div className="create-segment__wrap__btn-wrap__label">
              {id ? "Edit" : "Create new"} segment
            </div>
            <div className="create-segment__wrap__btn">
              <BorderSecondary type="button" onClick={handleDiscard}>
                Discard
              </BorderSecondary>

              <PrimaryButton
                variant="contained"
                onClick={() => !isPendingCreateSegment && openSegmentDialog()}
              >
                {isPendingCreateSegment ? "Loading..." : "Save segment"}
              </PrimaryButton>
            </div>
          </div>
          <div className="create-segment__dataset-wrapper">
            <div className="home__dataset-sec">
              <Controller
                control={control}
                name="country"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <div className="view-all-attributes__dataset-wrapper__label">Market:</div>
                      <DatasetDropdown
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                      {error?.message && (
                        <div className="create-segment__input-wrap__segment-err">
                          <img src={errorIcon} alt="Error Icon" />
                          {error?.message}
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
          </div>
          <div className="create-segment__header-divider"></div>
        </div>
        <InsightsWrap
          title="Segment insights:"
          insights={
            id
              ? [
                  {
                    key: "uniqueConsumers",
                    label: "Unique Consumers",
                    value: count,
                    requiresRefresh: false,
                  },
                  {
                    key: "engagedConsumers",
                    label: "Engaged Consumers",
                    value: engaged,
                    requiresRefresh: false,
                  },
                  {
                    key: "reachableConsumers",
                    label: "Reachable Consumers",
                    value: reachable,
                    requiresRefresh: false,
                  },
                  {
                    key: "uniqueEmails",
                    label: "Unique Emails",
                    value: uniqueEmails,
                    requiresRefresh: false,
                  },
                ]
              : []
          }
        />
        <div className="create-segment__divider"></div>
        <div className="create-segment__input-wrap">
          <form>
            <div className="form-control-container">
              <Controller
                control={control}
                name="segmentName"
                rules={{
                  required: "Please provide segment name",
                  pattern: {
                    value: /^(?!\d)[A-Za-z0-9 _ -]+$/,
                    message:
                      "Only letters, numbers, underscore (_), hyphen (-) and space are allowed. Cannot start with a number.",
                  },
                  maxLength: {
                    value: 100,
                    message: "Segment name cannot exceed 100 characters",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <label ref={field.ref} className="create-segment__insights-wrap__head">
                        Segment name:
                        <TextField
                          {...field}
                          value={field.value}
                          inputRef={segmentNameInputRef}
                          className="form-control-container__text-field"
                          required
                          margin="dense"
                          fullWidth
                          variant="outlined"
                          placeholder="Add segment name, e.g. Database Market Brand"
                          style={{ marginTop: "12px" }}
                          slotProps={{
                            input: {
                              inputProps: { maxLength: 100 },
                            },
                          }}
                          onKeyDown={(e) => {
                            if (!shouldForceStiristaPrefix) return;

                            const input = e.currentTarget as HTMLInputElement;
                            const current = String(input.value || "");
                            const selectionEl = segmentNameInputRef.current ?? input;
                            const start = selectionEl.selectionStart ?? current.length;
                            const end = selectionEl.selectionEnd ?? current.length;
                            const prefixLen = STIRISTA_PREFIX.length;

                            // Prevent deleting the whole locked prefix when only prefix remains.
                            if ((e.key === "Backspace" || e.key === "Delete") && current === STIRISTA_PREFIX) {
                              e.preventDefault();
                              requestAnimationFrame(() => {
                                const el = segmentNameInputRef.current;
                                if (!el) return;
                                el.setSelectionRange(prefixLen, prefixLen);
                              });
                              return;
                            }

                            const hasSelection = start !== end;
                            const selectionTouchesPrefix = start < prefixLen;

                            // If selection overlaps prefix, keep prefix and only remove suffix part.
                            if ((e.key === "Backspace" || e.key === "Delete") && hasSelection && selectionTouchesPrefix) {
                              e.preventDefault();
                              const next = `${STIRISTA_PREFIX}${current.slice(Math.max(end, prefixLen))}`.slice(
                                0,
                                100
                              );
                              setValue("segmentName", next, { shouldDirty: true, shouldTouch: true });
                              requestAnimationFrame(() => {
                                const el = segmentNameInputRef.current;
                                if (!el) return;
                                el.setSelectionRange(prefixLen, prefixLen);
                              });
                              return;
                            }

                            if (e.key === "Backspace" && !hasSelection && start <= prefixLen) {
                              e.preventDefault();
                              requestAnimationFrame(() => {
                                const el = segmentNameInputRef.current;
                                if (!el) return;
                                el.setSelectionRange(prefixLen, prefixLen);
                              });
                              return;
                            }

                            if (e.key === "Delete" && !hasSelection && start < prefixLen) {
                              e.preventDefault();
                              requestAnimationFrame(() => {
                                const el = segmentNameInputRef.current;
                                if (!el) return;
                                el.setSelectionRange(prefixLen, prefixLen);
                              });
                              return;
                            }
                          }}
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const raw = input.value;
                            const selectionStart = input.selectionStart ?? raw.length;

                            let next = raw;
                            if (shouldForceStiristaPrefix) {
                              if (
                                raw.length < STIRISTA_PREFIX.length &&
                                STIRISTA_PREFIX.startsWith(raw)
                              ) {
                                next = STIRISTA_PREFIX;
                              } else {
                                const suffix = raw.startsWith(STIRISTA_PREFIX) ? raw.slice(STIRISTA_PREFIX.length) : raw;
                                next = `${STIRISTA_PREFIX}${suffix}`.slice(0, 100);
                              }
                              stiristaSegmentPrefixAutoInserted.current = true;
                            }

                            if (next.length === 100) {
                              setError("segmentName", {
                                type: "maxLength",
                                message: "Segment name cannot exceed 100 characters",
                              });
                            } else {
                              clearErrors("segmentName");
                            }

                            field.onChange(next);

                            if (shouldForceStiristaPrefix) {
                              requestAnimationFrame(() => {
                                const el = segmentNameInputRef.current;
                                if (!el) return;
                                const delta = next.length - raw.length;
                                const nextPos = Math.max(
                                  STIRISTA_PREFIX.length,
                                  Math.min(selectionStart + delta, el.value.length)
                                );
                                el.setSelectionRange(nextPos, nextPos);
                              });
                            }
                          }}
                        />
                      </label>

                      {error?.message && (
                        <div className="create-segment__input-wrap__segment-err">
                          <img src={errorIcon} alt="Error Icon" />
                          {error?.message}
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
          </form>
        </div>

        <div className="create-segment__divider"></div>

        <div className="create-segment__dropdown-wrap">
          <div className="create-segment__dropdown-wrap__label">
            Include consumers with {isAndOrInclude === "OR" ? "ANY" : "ALL"}{" "}
            {isAndOrInclude === "OR" ? (
              <img src={andIcon} alt="and/or" />
            ) : (
              <img src={andOricon} alt="and" />
            )}{" "}
            of the following…
          </div>
        </div>

        {fields?.map((filter, index) => (
          <div key={filter.id} className="create-segment__wrap-dynamic-nested-dropdown">
            {index > 0 && (
              <div className="dropdown__switch">
                <div className="dropdown__switch__wrap">
                  <div
                    className={`dropdown__switch__wrap__and ${
                      filter.operator === "AND" ? "active" : ""
                    }`}
                    onClick={() => updateLogic(index, "AND")}
                  >
                    And
                  </div>
                  <div
                    className={`dropdown__switch__wrap__or ${
                      filter.operator === "OR" ? "active" : ""
                    }`}
                    onClick={() => updateLogic(index, "OR")}
                  >
                    Or
                  </div>
                </div>
              </div>
            )}

            <DynamicNestedDropdownSegmentSection
              dataDropdown={filteredDataDropdown}
              controlName="sections"
              indexSection={index}
              removeSection={() => {
                setStateRemoveIncludeSection(index as any);
              }}
              cloneSection={() => handleCloneSection(index, "sections")}
              // moveSection={() => handleMoveSection(index, "sections")}
            />
          </div>
        ))}

        {getValues(`sections.0.conditions`)?.length > 0 && (
          <>
            <div className="flex items-center cursor-pointer">
              <img src={plusRedIcon} alt="Add" />
              <span className="text-[#E70070] font-bold" onClick={() => handleAddSection()}>
                Add section
              </span>
            </div>
            <div className="create-segment__divider"></div>

            {fieldsExclusions?.length > 0 && (
              <div className="create-segment__dropdown-wrap">
                <div className="create-segment__dropdown-wrap__label">
                  Exclude {isAndOrExclude === "OR" ? "ANY" : "ALL"} consumers{" "}
                  {isAndOrExclude === "OR" ? (
                    <img src={andIcon} alt="and/or" />
                  ) : (
                    <img src={andOricon} alt="and" />
                  )}{" "}
                  of the following…
                </div>
              </div>
            )}

            {fieldsExclusions?.map((filter, index) => (
              <div key={filter.id} className="create-segment__wrap-dynamic-nested-dropdown">
                {index > 0 && (
                  <div className="dropdown__switch">
                    <div className="dropdown__switch__wrap">
                      <div
                        className={`dropdown__switch__wrap__and ${
                          filter.operator === "AND" ? "active" : ""
                        }`}
                        onClick={() => updateLogicExclusion(index, "AND")}
                      >
                        And
                      </div>
                      <div
                        className={`dropdown__switch__wrap__or ${
                          filter.operator === "OR" ? "active" : ""
                        }`}
                        onClick={() => updateLogicExclusion(index, "OR")}
                      >
                        Or
                      </div>
                    </div>
                  </div>
                )}
                <DynamicNestedDropdownSegmentSection
                  controlName="exclusions"
                  dataDropdown={filteredDataDropdown}
                  indexSection={index}
                  removeSection={() => setStateRemoveExcludeSection(index as any)}
                  cloneSection={() => handleCloneSection(index, "exclusions")}
                />
              </div>
            ))}

            <div className="flex items-center cursor-pointer">
              <img src={plusRedIcon} alt="Add" />
              <span className="text-[#E70070] font-bold" onClick={() => handleAddExclusions()}>
                Add exclusions
              </span>
            </div>
          </>
        )}
      </div>
      <DialogComponent
        open={showUnsavedWorkDialog.show}
        onClose={handleSubmitPrimary}
        handleSubmitSecondary={handleDiscardClick}
        loading={isPendingCreateSegment}
        type={showUnsavedWorkDialog.type}
        handleSubmitPrimary={handleSubmitPrimary}
      />
      <SegmentDialog
        open={isSegmentDialogOpen}
        onClose={closeSegmentDialog}
        onSegmentSelect={handleSubmit(handleSaveSegment)}
        actionType="saveSegment"
        onTagSelect={(tag) => setSelectedTag(tag)}

      />
      {(isPendingCreateSegment) && (
        <div className="freeze-overlay">
          <div className="freeze-overlay__spinner">
            <CircularProgressCustom />
            <div style={{ color: "#333", fontWeight: 500, marginTop: "8px" }}>
              Please wait...
            </div>
          </div>
        </div>
      )}
    </FormProvider>
  );
};

export default CreateSegment;
